// src/components/profiles/GuestUser/components/header/GuestProfileHeader.tsx

"use client";

import React, { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Mail,
  Lock,
  Cake,
  AtSign,
  CalendarCheck,
  IdCard,
  MapPin,
  Languages,
} from "lucide-react";

import { GuestProfileType } from "../../../../../types/profiles/guestProfileTypes";

import {
  AvatarDisplay,
  Modal,
  RichTextViewer,
} from "../../../../common";

import MyProfileActions from "../../../../shared/profile-actions/MyProfileActions";
import { isRTLText } from "../../../../../utils/helpers/languageHelpers";

import "./GuestProfileHeader.css";

interface GuestProfileHeaderProps {
  guest: GuestProfileType;
  onOpenFriends?: () => void;
}

const formatDate = (value?: string | null) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return value;
  }
};

const humanizeLanguage = (value?: string | null) => {
  if (!value) return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .trim();
};

const GuestProfileHeader: React.FC<GuestProfileHeaderProps> = ({
  guest,
  onOpenFriends,
}) => {
  const router = useRouter();

  const [openBiography, setOpenBiography] = useState(false);

  const user = guest.user;

  const displayName = useMemo(() => {
    const fullName =
      [user?.name, user?.family]
        .filter(Boolean)
        .join(" ")
        .trim();

    return fullName || "";
  }, [user?.name, user?.family]);

  const profileImageUrl = user?.avatar_cdn_url ?? "";
  const placeholderSrc =
    user?.avatar_url ??
    user?.profile_image ??
    null;

  const labelColor =
    user?.label?.color ||
    "var(--border-undisclosed)";

  const birthdayText = formatDate(user?.birthday);

  const joinedText =
    formatDate(guest?.register_date) ||
    formatDate(user?.registration_started_at);

  const primaryLanguage =
    user?.primary_language_display ||
    humanizeLanguage(user?.primary_language);

  const secondaryLanguage =
    user?.secondary_language_display ||
    humanizeLanguage(user?.secondary_language);

  const languageText =
    [primaryLanguage, secondaryLanguage]
      .filter(Boolean)
      .join(" • ");

  const countryText =
    user?.country_display ||
    user?.country ||
    "";

  const hasVisibleEmail =
    !!user?.email &&
    !!user?.show_email;

  const hasHiddenEmail =
    !!user?.email &&
    !user?.show_email;

  const hasVisibleCountry =
    !!countryText &&
    !!user?.show_country;

  const hasHiddenCountry =
    !!countryText &&
    !user?.show_country;

  const hasBiography =
    !!guest?.biography?.trim();

  return (
    <>
      <section className="guest-profile-header tl-indicator-aurora-light">

        <div className="guest-profile-header__avatar-wrap">
          <AvatarDisplay
            userId={user?.id}
            src={profileImageUrl}
            placeholderSrc={placeholderSrc}
            alt={`${displayName || user?.username}`}
            title={user?.username || "Guest User"}
            shape="rounded"
            size="mega"
            withBorder
            borderColor={labelColor}
            type="user"
            isVerifiedIdentity={user?.is_verified_identity ?? false}
            isTownlitVerified={user?.is_townlit_verified ?? false}
          />
        </div>

        <div className="guest-profile-header__details">

          {displayName && (
            <div
              className="guest-profile-header__row"
              dir={
                isRTLText(displayName)
                  ? "rtl"
                  : "ltr"
              }
            >
              <IdCard size={18} />
              <span className="guest-profile-header__name">
                {displayName}
              </span>
            </div>
          )}

          {user?.username && (
            <div className="guest-profile-header__row">
              <AtSign size={18} />
              <span>{user.username}</span>
            </div>
          )}

          {birthdayText && (
            <div className="guest-profile-header__row">
              <Cake size={18} />
              <span>{birthdayText}</span>
            </div>
          )}

          {languageText && (
            <div className="guest-profile-header__row">
              <Languages size={18} />
              <span>{languageText}</span>
            </div>
          )}

          {hasVisibleCountry && (
            <div className="guest-profile-header__row">
              <MapPin size={18} />
              <span>{countryText}</span>
            </div>
          )}

          {hasHiddenCountry && (
            <div className="guest-profile-header__row">
              <MapPin size={18} />
              <Lock size={16} />
            </div>
          )}

          {hasVisibleEmail && (
            <div className="guest-profile-header__row">
              <Mail size={18} />
              <span>{user.email}</span>
            </div>
          )}

          {hasHiddenEmail && (
            <div className="guest-profile-header__row">
              <Mail size={18} />
              <Lock size={16} />
            </div>
          )}

          {joinedText && (
            <div className="guest-profile-header__row">
              <CalendarCheck size={18} />
              <span>
                Joined {joinedText}
              </span>
            </div>
          )}
        </div>

        {hasBiography && (
          <div className="guest-profile-header__about">

            <h2 className="guest-profile-header__about-title">
              Biography
            </h2>

            <button
              type="button"
              className="guest-profile-header__bio-button"
              onClick={() => setOpenBiography(true)}
            >
              <div
                className="guest-profile-header__bio-preview"
                dir={
                  isRTLText(guest.biography || "")
                    ? "rtl"
                    : "ltr"
                }
              >
                <RichTextViewer
                  value={guest.biography}
                  appearance="light"
                />
              </div>
            </button>

          </div>
        )}

        <MyProfileActions
          onEditProfile={() =>
            router.push("/settings/information")
          }
          onOpenFriends={onOpenFriends}
          onOpenRequests={() =>
            router.push(
              "/settings/friendships?tab=requests"
            )
          }
        />

      </section>

      <Modal
        isOpen={openBiography}
        onClose={() => setOpenBiography(false)}
        title="Biography"
        hideFooter
      >
        <RichTextViewer
          value={guest.biography}
          placeholder="No biography yet."
        />
      </Modal>
    </>
  );
};

export default GuestProfileHeader;